// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { DocumentType, ErrorCause, ExpenseType } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// translations
import { translateError } from '../../../utils/translations';

// props
export interface DialogUpdateExpenseTypesProps {
	expenseTypes: ExpenseType[];
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
}

export const DialogUpdateExpenseTypes = ({
	expenseTypes,
	isOpen,
	onClose,
}: DialogUpdateExpenseTypesProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		// set loading
		setIsLoading(true);

		const promises = [];

		// for each expense type
		for (let i = 0; i < expenseTypes.length; i++) {
			const docTypes = expenseTypes[i].documentTypes as DocumentType[];

			const fetchUpdateExpenseType = fetch(
				`${process.env.REACT_APP_API_URL}/expense-types/${expenseTypes[i].id}`,
				{
					method: 'PATCH',
					body: JSON.stringify({
						documentTypes: docTypes.map((dt) => {
							return {
								id: dt.id,
								isPrivate: dt.isPrivate,
								isRequired: dt.isRequired,
							};
						}),
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);

			promises.push(fetchUpdateExpenseType);
		}

		Promise.all(promises)
			.then(async (requests) => {
				const responses = await Promise.all(requests.map((req) => req.json()));
				const responseWithError = responses.find((res) => res.error);
				if (responseWithError) {
					throw new Error(responseWithError.error.message || t('error.expenseTypeUpdateFailed'), {
						cause: {
							id: responseWithError.error.id || 'EXPENSE_TYPE_PATCH_FAILED',
							message: responseWithError.error.message || t('error.expenseTypeUpdateFailed'),
							details: responseWithError.error.details || {},
						},
					});
				}
				// set banner
				setBanner({
					hasClose: true,
					message: t('alert.expenseTypesUpdated'),
					severity: 'success',
				});

				// close dialog
				if (onClose) {
					onClose();
				}
			})
			.catch((error) => {
				const err = error as Error & { cause: ErrorCause };
				const cause = err.cause;
				setError(translateError({ cause, t }));
			})
			.finally(() => {
				// clear loading
				setIsLoading(false);
			});
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('saveExpenseTypes'),
					loading: isLoading,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{ type: 'expenseType' }}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			title={{
				children: t('saveExpenseTypes'),
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography>{t('dialogUpdateExpenseTypes.content')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{t('alert.updateExpenseTypes')}</Alert>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
